import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import useIsMobile from '../hooks/useIsMobile';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/auth/AuthComponents';
import { useSubscription } from '../hooks/useSubscription';
import UnauthorizedBox from '../components/shared/UnauthorizedBox';
import { useNavigate, useLocation } from 'react-router-dom';

export default function JobBoardPage() {
  const { isAdmin, isAuthenticated } = useAuth();
  const { loading: subLoading } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [remote, setRemote] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ jobs: [], total: 0 });
  const [matchesData, setMatchesData] = useState({ jobs: [], total: 0 });
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'matches'
  const isMobile = useIsMobile();

  // Determine active tab based on URL
  useEffect(() => {
    if (location.pathname === '/my-job-match') {
      setActiveTab('matches');
    } else {
      setActiveTab('all');
    }
  }, [location.pathname]);

  // All signed-in users get access; match counts are enforced per plan on the backend.
  const hasAccess = isAdmin || isAuthenticated;


  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (locationFilter) p.set('location', locationFilter);
    if (remote !== '') p.set('remote', remote);
    p.set('page', String(page));
    p.set('per_page', String(perPage));
    return p.toString();
  }, [q, locationFilter, remote, page, perPage]);

  const matchesParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(page));
    p.set('per_page', String(perPage));
    return p.toString();
  }, [page, perPage]);

  // Fetch all jobs
  useEffect(() => {
    if (!hasAccess || activeTab !== 'all') return;
    let ignore = false;
    async function fetchJobs() {
      setLoading(true); setError('');
      try {
        const json = await apiRequest(`jobs/board?${params}`);
        if (!ignore) setData(json);
      } catch (e) {
        if (!ignore) setError(String(e.message || e));
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    fetchJobs();
    return () => { ignore = true; };
  }, [params, hasAccess, activeTab]);

  // Fetch job matches
  useEffect(() => {
    if (!hasAccess || activeTab !== 'matches') return;
    let ignore = false;
    async function fetchMatches() {
      setMatchesLoading(true); setError('');
      try {
        const json = await apiRequest(`jobs/matching?${matchesParams}`);
        if (!ignore) setMatchesData(json);
      } catch (e) {
        if (!ignore) setError(String(e.message || e));
      } finally {
        if (!ignore) setMatchesLoading(false);
      }
    }
    fetchMatches();
    return () => { ignore = true; };
  }, [matchesParams, hasAccess, activeTab]);

  const currentData = activeTab === 'matches' ? matchesData : data;
  const currentLoading = activeTab === 'matches' ? matchesLoading : loading;
  const totalPages = Math.max(1, Math.ceil((currentData.total || 0) / perPage));

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    if (tab === 'matches') {
      navigate('/my-job-match');
    } else {
      navigate('/jobs');
    }
  };

  if (!isAuthenticated && !subLoading) {
    return (
      <UnauthorizedBox
        title="Sign in required"
        message="Sign in to browse the job board and view your AI job matches."
        upgradeMessage=""
        featureName="Job Board"
      />
    );
  }

  if (subLoading) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--color-text-muted)]">Loading...</div>;
  }

  if (!hasAccess) {
    return (
      <UnauthorizedBox
        title="Job Board Access Required"
        message="Sign in to browse jobs and view personalized matches."
        upgradeMessage="Upgrade your plan for more job matches per run."
        featureName="Job Board"
      />
    );
  }

  return (
    <div className="min-h-full text-[var(--color-text)] bg-[var(--color-bg)]">
      <Helmet>
        <title>Job Board – DocxsAi</title>
        <meta name="description" content="Browse curated job vacancies. Search by title, location, remote, and type." />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 py-6 md:py-10">
        <h1 className="text-2xl md:text-3xl font-extrabold text-[var(--color-text)] mb-4 md:mb-6">
          Job{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
            Board
          </span>
        </h1>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6">
          {[
            { id: 'all', label: 'All Jobs', count: data.total || 0 },
            { id: 'matches', label: 'My Matches', count: matchesData.total || 0 },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)] border border-[var(--color-primary)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] border border-transparent'
              }`}
            >
              {tab.id === 'matches' ? tab.label : `${tab.label} (${tab.count})`}
            </button>
          ))}
        </div>

        {/* Search filters - only show for All Jobs tab */}
        {activeTab === 'all' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
            <input className="themed-input rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-[var(--color-ring)] w-full text-[var(--color-text)]" placeholder="Search title/company/location" value={q} onChange={(e)=>{setPage(1); setQ(e.target.value);}} />
            <input className="themed-input rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-[var(--color-ring)] w-full text-[var(--color-text)]" placeholder="Location" value={locationFilter} onChange={(e)=>{setPage(1); setLocationFilter(e.target.value);}} />
            <select className="themed-input rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-[var(--color-ring)] w-full text-[var(--color-text)]" value={remote} onChange={(e)=>{setPage(1); setRemote(e.target.value);}}>
              <option value="">Remote or Onsite</option>
              <option value="true">Remote</option>
              <option value="false">Onsite/Hybrid</option>
            </select>
          </div>
        )}

        {currentLoading && <div className="text-[var(--color-text-secondary)]">Loading {activeTab === 'matches' ? 'matches' : 'jobs'}...</div>}
        {error && <div className="text-[var(--color-danger)]">{error}</div>}

        {/* Show notification for no matches */}
        {!currentLoading && !error && activeTab === 'matches' && (currentData.jobs || []).length === 0 ? (
          <div className="elevated-card rounded-lg p-6">
            <h2 className="text-xl font-semibold text-[var(--color-text)] mb-4">Your Job Matches</h2>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-24 h-24 bg-[var(--color-surface-muted)] rounded-full flex items-center justify-center mb-4">
                <span className="text-4xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold text-[var(--color-text)] mb-2">No job matches found</h3>
              <p className="text-[var(--color-text-muted)] mb-6 max-w-md">
                Start by analysing your CV to get jobs that matches here or explore all available jobs.
              </p>
              <button
                className="brand-gradient-btn px-6 py-3 rounded-lg font-semibold"
                onClick={() => handleTabChange('all')}
              >
                Browse All Jobs
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-3 md:gap-4">
              {(currentData.jobs || []).map(job => (
              <div key={job.id} className="elevated-card rounded-xl p-3 md:p-4 hover:border-[var(--color-border-subtle)] transition">
                <div className="flex md:flex-row flex-col items-start gap-3 md:gap-4">
                  {activeTab === 'matches' ? (
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-[var(--color-surface-muted)] rounded flex items-center justify-center text-[var(--color-text-muted)]">🎯</div>
                  ) : job.logo_url ? (
                    <img src={job.logo_url} alt={job.company || ''} className="w-10 h-10 md:w-12 md:h-12 object-contain rounded" />
                  ) : (
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-[var(--color-surface-muted)] rounded flex items-center justify-center text-[var(--color-text-muted)]">🏢</div>
                  )}
                  <div className="flex-1 w-full">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg md:text-xl font-bold text-[var(--color-text)] mr-2">{job.title}</h2>
                      {job.company && <span className="text-[var(--color-text-secondary)]">• {job.company}</span>}
                    </div>
                    <div className="text-[var(--color-text-muted)] text-sm mt-1">
                      <span>{job.location || 'Location not specified'}</span>
                      {job.is_remote ? <span className="ml-2 px-2 py-0.5 bg-emerald-500/15 text-[var(--color-success)] rounded">Remote</span> : null}
                      {job.employment_type && <span className="ml-2 px-2 py-0.5 bg-[var(--color-primary-soft)] text-[var(--color-primary)] rounded">{job.employment_type}</span>}
                    </div>
                    {job.salary_text && <div className="text-[var(--color-text-secondary)] text-sm mt-1">{job.salary_text}</div>}
                    {job.description_html && (
                      <div className="prose max-w-none text-[var(--color-text-secondary)] mt-2" dangerouslySetInnerHTML={{ __html: (job.description_html || '').slice(0, isMobile ? 220 : 420) + '...' }} />
                    )}
                  </div>
                  {job.apply_url && (
                    <>
                      <a href={job.apply_url} target="_blank" rel="noreferrer" className="hidden md:inline-flex ml-4 px-4 py-2 brand-gradient-btn rounded-lg">Apply</a>
                      <a href={job.apply_url} target="_blank" rel="noreferrer" className="md:hidden w-full mt-2 px-4 py-2 brand-gradient-btn rounded-lg text-center">Apply</a>
                    </>
                  )}
                </div>
              </div>
            ))}
            </div>

            <div className="flex items-center justify-between mt-6">
              <div className="text-[var(--color-text-muted)] text-sm">Total: {currentData.total}</div>
              <div className="flex items-center gap-2">
                <button disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))} className="px-3 py-1.5 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] border border-[var(--color-border)] disabled:opacity-50">Prev</button>
                <span className="text-[var(--color-text-secondary)] text-sm">Page {page} of {totalPages}</span>
                <button disabled={page>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))} className="px-3 py-1.5 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] border border-[var(--color-border)] disabled:opacity-50">Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
